import type { IMediaNode } from '../../collection-subtitles/media-node';
import { fetchTreeApi } from '../fetchTree';
import type { ICropFileInfo } from '../listCrops';
import { listCropsApi } from '../listCrops';

export interface ICropWithSource extends ICropFileInfo {
  sourceTitle: string;
  sourceType: string;
}

function collectLeafPaths(
  nodes: IMediaNode[],
  parentPath: string,
): { path: string; title: string; type: string }[] {
  const results: { path: string; title: string; type: string }[] = [];
  for (const node of nodes) {
    const nodePath = parentPath ? `${parentPath}/${node.slug}` : node.slug;
    if (node.children.length === 0) {
      results.push({ path: nodePath, title: node.title, type: node.type });
    } else {
      results.push(...collectLeafPaths(node.children, nodePath));
    }
  }
  return results;
}

export async function fetchAllCropsApi(): Promise<ICropWithSource[]> {
  const tree = await fetchTreeApi();
  const allCrops: ICropWithSource[] = [];

  const categories = ['movies', 'series', 'talks', 'news', 'documentaries', 'podcasts', 'music'];
  const leaves: { path: string; title: string; type: string }[] = [];

  for (const category of categories) {
    const categoryNodes = tree.filter((n) => n.type === getCategoryType(category));
    leaves.push(...collectLeafPaths(categoryNodes, category));
  }

  const results = await Promise.allSettled(
    leaves.map(async (leaf) => {
      const resp = await listCropsApi(leaf.path);
      return resp.files.map((f) => ({ ...f, sourceTitle: leaf.title, sourceType: leaf.type }));
    }),
  );

  for (const result of results) {
    if (result.status === 'fulfilled') {
      allCrops.push(...result.value);
    }
  }

  return allCrops;
}

function getCategoryType(category: string): string {
  const map: Record<string, string> = {
    movies: 'movie',
    series: 'series',
    talks: 'talk',
    news: 'news',
    documentaries: 'documentary',
    podcasts: 'podcast',
    music: 'music',
  };
  return map[category] ?? category;
}
